import React from 'react';
import { Link } from 'react-router-dom';
import { jobCategoriesAPI } from '../services/api';
import { JobCategory } from '../types';


const HOVER_OPEN_DELAY_MS = 150;
const HOVER_CLOSE_DELAY_MS = 200;


const CategoryMegaNav: React.FC = () => {
  const [tree, setTree] = React.useState<JobCategory[]>([]);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [activeParentId, setActiveParentId] = React.useState<number | null>(null);
  const openTimerRef = React.useRef<number | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    let cancelled = false;
    jobCategoriesAPI.getTree({ withCounts: true })
      .then((res) => {
        if (cancelled) return;
        const categories = res.categories || [];
        setTree(categories);
        if (categories.length > 0) setActiveParentId(categories[0].id);
      })
      .catch(() => { if (!cancelled) setLoadFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const canHover = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;  // SSR-safe default
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  };

  const clearTimers = () => {
    if (openTimerRef.current) { window.clearTimeout(openTimerRef.current); openTimerRef.current = null; }
    if (closeTimerRef.current) { window.clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  };
  const scheduleOpen = () => {
    if (!canHover()) return;  // touch device — click-only
    clearTimers();
    openTimerRef.current = window.setTimeout(() => setOpen(true), HOVER_OPEN_DELAY_MS);
  };
  const scheduleClose = () => {
    if (!canHover()) return;  // touch device — click-only
    clearTimers();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  if (loading) return null;
  if (loadFailed || tree.length === 0) {
    return <Link to="/jobs" className="category-mega-nav__fallback">Categories</Link>;
  }

  const activeParent = tree.find((p) => p.id === activeParentId) ?? tree[0];

  return (
    <div
      ref={containerRef}
      className="category-mega-nav"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
    >
      <button
        ref={triggerRef}
        type="button"
        className="category-mega-nav__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls="category-mega-nav-panel"
        onClick={() => setOpen((prev) => !prev)}
      >
        Categories &#9662;
      </button>
      {open && (
        <div
          id="category-mega-nav-panel"
          role="menu"
          className="category-mega-nav__panel"
        >
          <div className="category-mega-nav__parents-rail">
            {tree.map((parent) => (
              <button
                key={parent.id}
                type="button"
                role="menuitem"
                aria-current={parent.id === activeParent.id ? 'true' : undefined}
                className={`category-mega-nav__parent ${parent.id === activeParent.id ? 'is-active' : ''}`}
                onMouseEnter={() => setActiveParentId(parent.id)}
                onFocus={() => setActiveParentId(parent.id)}
              >
                <span>{parent.name}</span>
                <span className="category-mega-nav__count">{parent.active_job_count ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="category-mega-nav__children-pane">
            <Link
              to={`/jobs/category/${activeParent.slug}`}
              className="category-mega-nav__parent-anchor"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              {activeParent.name} &mdash; all &rarr;
            </Link>
            <div className="category-mega-nav__children-grid">
              {(activeParent.children || []).map((child) => (
                <Link
                  key={child.id}
                  to={`/jobs/category/${activeParent.slug}/${child.slug}`}
                  role="menuitem"
                  className="category-mega-nav__child"
                  onClick={() => setOpen(false)}
                >
                  <div className="category-mega-nav__child-name">{child.name}</div>
                  <div className="category-mega-nav__child-count">
                    {child.active_job_count ?? 0} open
                  </div>
                </Link>
              ))}
              {(activeParent.children || []).length === 0 && (
                <div className="muted">
                  No open roles right now. <Link to="/jobs">Browse all jobs</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryMegaNav;
